import {
  guessLanguageAsync,
  type LanguageResult,
} from '@bsky.app/expo-guess-language'

type LanguageSuggestion = {
  language: string
  confidence: number
}

/**
 * Card language detection for an Entourage — TITLE FIRST.
 *
 * The card language must reflect the language the creator actually WROTE.
 * The title is the 1st (and normally only) signal: it is short, deliberate,
 * and always present. The description is used ONLY when the title is empty
 * or undetectable — never mixed with the title, so a stray foreign-language
 * description can never override the title (e.g. an Arabic title with a
 * Chinese description stays Arabic).
 *
 * Detection runs on the Finish click and is time-boxed: it must resolve
 * within ~1.5s or we fall back immediately — a slow detector must never
 * delay or break creating the Entourage.
 */
export const GUESS_STARTER_PACK_MIN_CONFIDENCE = 0.4
export const GUESS_STARTER_PACK_MARGIN = 0.12
export const GUESS_STARTER_PACK_TIMEOUT_MS = 1_500

//
// Gibberish gates — a title like "kjkkjj" is not a language. The detector
// WILL return a confident answer for any letters (lande: kjkkjj → Bengali
// 0.60, hhh → Vietnamese 0.99), so it is gated on plausibility BEFORE the
// result can reach the record. Gates are pure string checks: zero latency.
//

/** Pure-Latin gibberish ("kjkkjj", "hhh") shows no voweling at all. */
const LATIN_VOWELS = /[aeiouyáéíóúàèìòùâêîôûäëïöüåøæœ]/i

/**
 * Non-Latin detection requires the text to actually contain that script —
 * e.g. a top candidate of Bengali on an all-Latin title is rejected. (A
 * language not listed here is a Latin-script language: implied consistent.)
 */
const LANG_SCRIPT_REQUIREMENTS: Record<string, RegExp> = {
  ar: /\p{Script=Arabic}/u,
  fa: /\p{Script=Arabic}/u,
  ur: /\p{Script=Arabic}/u,
  ps: /\p{Script=Arabic}/u,
  ckb: /\p{Script=Arabic}/u,
  he: /\p{Script=Hebrew}/u,
  el: /\p{Script=Greek}/u,
  ru: /\p{Script=Cyrillic}/u,
  uk: /\p{Script=Cyrillic}/u,
  be: /\p{Script=Cyrillic}/u,
  sr: /\p{Script=Cyrillic}/u,
  bg: /\p{Script=Cyrillic}/u,
  hi: /\p{Script=Devanagari}/u,
  mr: /\p{Script=Devanagari}/u,
  ne: /\p{Script=Devanagari}/u,
  bn: /\p{Script=Bengali}/u,
  zh: /\p{Script=Han}/u,
  ja: /\p{Script=Han}|\p{Script=Hiragana}|\p{Script=Katakana}/u,
  ko: /\p{Script=Hangul}/u,
  ka: /\p{Script=Georgian}/u,
  hy: /\p{Script=Armenian}/u,
}

/**
 * True when the text cannot plausibly BE a language: fewer than 3 letters,
 * or pure-Latin text with no vowels. Such titles never reach the detector —
 * they fall back to the record/app language instead of a nonsense lang.
 */
function isGibberish(text: string): boolean {
  const letters = text.match(/\p{L}/gu) ?? []
  if (letters.length < 3) return true
  const latinLetters = letters.filter(l => /\p{Script=Latin}/u.test(l))
  if (latinLetters.length === letters.length && !LATIN_VOWELS.test(text)) {
    return true
  }
  return false
}

/** Detected language must match the script actually present in the text. */
function scriptConsistent(lang: string, text: string): boolean {
  const requirement = LANG_SCRIPT_REQUIREMENTS[lang]
  if (!requirement) return true
  return requirement.test(text)
}

/**
 * Detect the card language for a title (description as a last-resort
 * fallback). Returns the BCP-47 base code (`ar`, `fa`, `en`, …) or `null`
 * when detection is not confident enough to override the fallback — or when
 * it takes too long / fails. Never throws.
 */
export async function guessStarterPackLang(
  title: string,
  description?: string,
): Promise<string | null> {
  // Title is the 1st priority: detect from it alone when it has content.
  const titleText = title.trim()
  const text = titleText || (description ?? '').trim()
  if (!text) return null
  // "kjkkjj" is not Bengali: gibberish titles skip detection entirely.
  if (isGibberish(text)) return null

  let suggestions: LanguageSuggestion[]
  try {
    suggestions = await withTimeout(guessLanguageAsync(text))
  } catch {
    return null
  }
  if (!suggestions || suggestions.length === 0) return null

  const [top, second] = suggestions
  const confidentEnough =
    top.confidence >= GUESS_STARTER_PACK_MIN_CONFIDENCE &&
    (second === undefined ||
      top.confidence - second.confidence >= GUESS_STARTER_PACK_MARGIN)
  if (!confidentEnough) return null

  const lang = top.language.split('-')[0].toLowerCase()
  // A confident Bengali answer on an all-Latin title is detector noise —
  // the script must match the text.
  if (!scriptConsistent(lang, text)) return null

  // Base language code only: the card layer normalizes regional variants.
  return lang
}

/** Reject after `ms` so a slow native module can never stall submit. */
function withTimeout<T>(p: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('guess-language timeout')),
      GUESS_STARTER_PACK_TIMEOUT_MS,
    )
    p.then(
      value => {
        clearTimeout(timer)
        resolve(value)
      },
      err => {
        clearTimeout(timer)
        reject(err)
      },
    )
  })
}

export type {LanguageResult}
